import * as React from "react";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import { Link } from "react-router-dom";
import { useSecurityContext } from "../../Core/SecurityContext";
import { Form, useFormApi } from "informedv4";
import DocumentTitle from "../../misc/components/DocumentTitle";
import { StudyForm, ExtractBase64Image, StudyFormComponent } from "./StudyForm";
import { StudyOverreadForm, OverreadForm } from "./StudyOverreadForm";
import { StudyMetricsForm } from "./StudyMetricsForm";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import { DeviceType } from "webportalclient";
import { DeviceTypes } from "../../misc/api/apiTypes";

export const ActionButtons = ({ submitting, automationIdPrefix, onSaveClick }) => {
    return (
        <div className="h2 pull-right">
            <button className="btn btn-primary" type="submit" disabled={submitting} onClick={onSaveClick} data-automationid={`${automationIdPrefix}_Save`}>
                Save
            </button>&nbsp;
            <Link className="btn btn-primary btn-outline" to="/studies" data-automationid={`${automationIdPrefix}_ReturnToList`}>
                Return to List
            </Link>
        </div>
    );
}

export const CreateStudyForm = () => {
    const client = useWebPortalApiClient();
    const [successAlert, setSuccessAlert] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);
    const [warningAlert, setWarningAlert] = React.useState<string>(null);
    const [errorAlert, setErrorAlert] = React.useState<string>(null);
    const [logoToUpload, setLogoToUpload] = React.useState<File>();
    const formApi = useFormApi();
    const legacyClient = useWebPortalLegacyClient();
    const [selectedDeviceTypes, setSelectedDeviceTypes] = React.useState<DeviceType[]>([]);


    const onSubmit = async () => {
        setSubmitting(true);
        setSuccessAlert(false);
        setWarningAlert(null);
        setErrorAlert(null);

        formApi.validate();
        const state = formApi.getFormState();

        if (state.invalid) {
            setSuccessAlert(false);
            setSubmitting(false);
            return setErrorAlert("Errors exist on the page");
        }


        try {
            let base64StringLogo;
            if (logoToUpload) {
                base64StringLogo = await ExtractBase64Image(logoToUpload);
            }

            const study = await client.client.postStudy({
                name: state.values.Name as string,
                protocol: state.values.Protocol as string,
                vitalRef: state.values.VitalRef as string,
                sessionTypeIds: state.values.SessionTypes !== undefined ? (state.values.SessionTypes as any).map(st => st.id) : [],
                countriesIds: state.values.Countries !== undefined ? (state.values.Countries as any).map(c => c.id) : [],
                languageIds: state.values.DiaryLanguage !== undefined ? (state.values.DiaryLanguage as any).map(l => l.localeName) : [],
                deviceTypeIds: state.values.DeviceTypes !== undefined ? (state.values.DeviceTypes as any).map(dt => dt.id) : [],
                description: state.values.Description as string,
                targetSubjects: state.values.TargetSubjects as number ?? 0,
                targetSessions: state.values.TargetSessions as number ?? 0,
                logo: base64StringLogo,
                logoMediaType: logoToUpload?.type,
                vitalographContactEmail: state.values.StudyContactEmail as string,
                diaryConfiguration: state.values.DiaryConfiguration as string
            });

            if (study) {
                await legacyClient.patchSetting("reporting.rootFolder", state.values.RootReportingFolder, study.id);

                setSuccessAlert(true);
            }
        } catch (ex) {
            setErrorAlert(ex.message !== undefined ? ex.message : ex.Message);
        }

        setSubmitting(false);
        window.scrollTo(0, 0);
    }

    return (
        <>
            <Alert show={successAlert} category={AlertCategoryEnum.Success}>
                Saved successfully. Return to{" "}
                <Link to={`/studies`}>Studies list</Link>?
            </Alert>
            <Alert show={warningAlert !== null} category={AlertCategoryEnum.Warning}>
                {warningAlert}
            </Alert>
            <Alert show={errorAlert !== null} category={AlertCategoryEnum.Danger}>
                {errorAlert}
            </Alert>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">Create Study</h2>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <div id="detailsPanel" className="panel panel-primary">
                        <div className="panel-heading">
                            <h4>Core Details</h4>
                        </div>
                        <div className="panel-body">
                            <StudyFormComponent onAlert={(alert) => setErrorAlert(alert)} onFileAttached={(file) => setLogoToUpload(file)} onDeviceTypeChanged={setSelectedDeviceTypes} />
                        </div>
                    </div>
                    <div id="overreadSetupPanel" className="panel panel-primary">
                        <div className="panel-heading">
                            <h4>Over-Read Setup</h4>
                        </div>
                        <div className="panel-body">
                            <StudyOverreadForm />
                        </div>
                    </div>
                    <div id="metricsPanel" className="panel panel-primary">
                        <div className="panel-heading">
                            <h4>Metrics</h4>
                        </div>
                        <div className="panel-body">
                            <StudyMetricsForm />
                        </div>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <ActionButtons submitting={submitting} onSaveClick={onSubmit} automationIdPrefix="ActionsFooter" />
                </div>
                </div>
            </>
        
    );
}

export const CreateStudyScreen = () => {
    const { currentUserPermissions } = useSecurityContext();
    const legacyClient = useWebPortalLegacyClient();


    return (
        <DocumentTitle title="Create Study | Vitalograph Web Portal">
            <Form validateOn="submit" noValidate initialValues={{ TargetSubjects: 0, TargetSessions: 0 }}>
                <CreateStudyForm />
            </Form>
        </DocumentTitle>
    );
}
